import React from 'react';
import GlobalStyles from '@nokia-csf-uxr/freeform-design-tokens/GlobalStyles';
import { GlobalCCFKStyles } from 'styles';
import { ComponentsContextProvider, getContext } from 'common';
import DefaultTheme from 'components/styles/themes/default-theme';
import DarkTheme from 'components/styles/themes/dark-theme';
import AAADefaultTheme from 'components/styles/themes/aaa-default-theme';
import AAADarkTheme from 'components/styles/themes/aaa-dark-theme';
import _isObject from 'lodash/isObject';
import _merge from 'lodash/merge';
import Styled from './styles';

const resolveTheme = (theme: string | object | undefined) => {
  let resolvedTheme: object = DefaultTheme;
  if (typeof theme === 'string') {
    switch (theme.toLowerCase()) {
      case 'dark':
        resolvedTheme = DarkTheme;
        break;
      case 'aaa-dark':
        resolvedTheme = AAADarkTheme;
        break;
      case 'aaa-default':
        resolvedTheme = AAADefaultTheme;
        break;
      case 'light':
      case 'default':
      default:
        resolvedTheme = DefaultTheme;
    }
    return resolvedTheme;
  } else if (_isObject(theme)) {
    return theme;
  }
  return resolvedTheme;
};

export interface AppProps extends React.ComponentPropsWithRef<'div'> {
  /** Children prop is where app content resides. All components in this slot will have access to the App container's context, styling and themes. */
  children: React.ReactNode;
  /** Toggle the `default` or `dark` theme by passing those strings to this prop, or provide your own custom theme by passing a theme object. Import `DefaultTheme`, `DarkTheme` from <App /> to see Theme object structure. */
  theme?: 'default' | 'dark' | 'aaa-default' | 'aaa-dark' | object | undefined;
  /** Top-level right-to-left prop that will propagate down the hierarchy tree to all children and set their direction. */
  rtl?: boolean;
  /** If true, motion animations are disabled.  */
  disableAnimation?: boolean;
}

/** App container component. This component should be the topmost parent in your app hierarchy (it contains global CCFK styles, theming, context). */
const App = React.forwardRef<HTMLElement, AppProps>((props, ref) =>
  getContext((context) => {
    const { children, theme, rtl, disableAnimation } = props;
    return (
      <ComponentsContextProvider
        theme={_merge({}, DefaultTheme, context.theme, theme === undefined ? {} : resolveTheme(theme))}
        direction={{ RTL: context.RTL || rtl }}
        disableAnimation={context.disableAnimation || disableAnimation}
      >
        {/* @ts-expect-error should be fixed in token repo */}
        <GlobalStyles />
        <GlobalCCFKStyles />
        <Styled.Container ref={ref}>
          {children}
        </Styled.Container>
      </ComponentsContextProvider>
    );
  }));

App.displayName = 'App';

App.defaultProps = {
  theme: undefined,
  rtl: false,
  disableAnimation: undefined,
};

export { App as default, DarkTheme, DefaultTheme, AAADefaultTheme, AAADarkTheme };
